import { useCallback } from "react"
import { useMedications } from "@/modules/patients/detail/medication/hooks/useMedications"
import { useRegenerateReferralDocument } from "@/modules/patients/detail/progress-note/hooks/useRegenerateReferralDocument"
import { buildChCarePlanData } from "@/modules/patients/detail/progress-note/lib/build-ch-care-plan-data"
import { isDateOlderThan24Hours } from "@/modules/patients/detail/progress-note/lib/is-date-older-than-24-hours"
import { getRecommendedMedications } from "@/modules/patients/detail/progress-note/lib/is-recommended-medications-empty"
import { ProgressNoteData } from "@/modules/patients/detail/progress-note/types/progress-note-fragment-type"
import { usePatientFileContext } from "@/modules/patients/detail/store/usePatientStore"
import { usePatientParam } from "@/modules/patients/hooks/usePatientParam"
import { chSaveCarePlanDataMutation$data } from "@/modules/patients/services/ch/__generated__/chSaveCarePlanDataMutation.graphql"
import { useChUpdateProgressNoteCareProviderMutation } from "@/modules/patients/services/ch/useChUpdateProgressNoteCareProviderMutation"
import { useSaveCarePlanDataMutation } from "@/modules/patients/services/useSaveCarePlanDataMutation"

type Args = {
  progressNote: ProgressNoteData
}

export type ChangeCareProviderResult = {
  carePlanPreview: chSaveCarePlanDataMutation$data | null
}

type ChangeArgs = {
  newCareProviderId: string | null
  onSuccess?: (result: ChangeCareProviderResult) => void
  onError?: () => void
}

const useChangeCareProviderFlow = ({ progressNote }: Args) => {
  const patientId = usePatientParam()
  const patient = usePatientFileContext((state) => state.patientFileInfo)

  const [updateProgressNoteCareProvider] = useChUpdateProgressNoteCareProviderMutation()
  const saveCarePlanData = useSaveCarePlanDataMutation()
  const { regenerateReferralDocument } = useRegenerateReferralDocument({ progressNote })

  const { activeMedications, inactiveMedications } = useMedications()

  const changeCareProvider = useCallback(
    ({ newCareProviderId, onSuccess, onError }: ChangeArgs) => {
      if (!newCareProviderId) {
        onError?.()
        return
      }

      const isWithin24HoursAfterClose = !isDateOlderThan24Hours(progressNote.appointment.completedAt ?? null)

      updateProgressNoteCareProvider({
        variables: {
          input: {
            id: progressNote.id,
            newCareProviderId,
            isWithin24HoursAfterClose,
          },
        },
        onCompleted: () => {
          const tasks: Promise<unknown>[] = [regenerateReferralDocument(true)]
          let carePlanPreview: chSaveCarePlanDataMutation$data | null = null

          if (progressNote.treatmentPlan && patient) {
            const { treatmentPlan } = progressNote

            const recommendedMedications = getRecommendedMedications(progressNote).map(({ node }) => {
              const { name, morningDosage, noonDosage, eveningDosage, nightDosage, usageInstructions, unit } =
                node.medication
              return {
                name,
                morningDosage,
                noonDosage,
                eveningDosage,
                nightDosage,
                usageInstruction: usageInstructions,
                asNeeded: unit === "PRN",
              }
            })

            const { medications, therapies } = buildChCarePlanData(progressNote, [
              ...activeMedications,
              ...inactiveMedications,
            ])

            tasks.push(
              saveCarePlanData({
                progressNoteId: progressNote.id,
                patientId,
                patient: {
                  firstName: patient.firstName,
                  lastName: patient.lastName,
                  birthDate: patient.birthDate,
                },
                appointmentId: progressNote.appointment.id,
                appointmentLanguage: progressNote.appointment.language,
                treatmentPlan: {
                  title: treatmentPlan.title,
                  from: treatmentPlan.from,
                  to: treatmentPlan.to,
                  recommendedTreatment: treatmentPlan.recommended ?? "",
                  followUpTreatment: treatmentPlan.followUpInstructions ?? "",
                },
                medications,
                recommendedMedications,
                therapies,
                careProviderId: newCareProviderId,
                keepOriginalAuthor: true,
              }).then((response) => {
                carePlanPreview = response
                return response
              }),
            )
          }

          Promise.allSettled(tasks).then(() => {
            onSuccess?.({ carePlanPreview })
          })
        },
        onError: () => {
          onError?.()
        },
      })
    },
    [
      progressNote,
      updateProgressNoteCareProvider,
      regenerateReferralDocument,
      saveCarePlanData,
      patient,
      patientId,
      activeMedications,
      inactiveMedications,
    ],
  )

  return { changeCareProvider }
}

export { useChangeCareProviderFlow }
